import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import EmptyState from '../components/common/EmptyState';
import Eyebrow from '../components/common/Eyebrow';
import { useCart } from '../context/CartContext';
import { useUI } from '../context/UIContext';
import { ROUTES } from '../constants/routes';

const Cart = () => {
  const navigate = useNavigate();
  const { items, removeItem, clearCart, totals } = useCart();
  const { showToast } = useUI();

  const HOME_FEE = 0;
  const grand = totals.subtotal + HOME_FEE;

  if (items.length === 0) {
    return (
      <Box sx={{ bgcolor: 'var(--cream)', minHeight: '80vh', pt: { xs: 10, md: 14 }, pb: 10 }}>
        <Container maxWidth="md">
          <EmptyState
            icon="🛒"
            title="Your cart is empty"
            description="Browse our tests and packages to add something to your cart."
            action={
              <Stack direction="row" spacing={1.5} justifyContent="center">
                <Button variant="contained" color="primary" onClick={() => navigate(ROUTES.TESTS)}>
                  Browse Tests
                </Button>
                <Button variant="outlined" onClick={() => navigate(ROUTES.PACKAGES)}>
                  View Packages
                </Button>
              </Stack>
            }
          />
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'var(--cream)', minHeight: '90vh' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid var(--border-l)', py: 3, pt: { xs: 10, md: 12 } }}>
        <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 4 } }}>
          <Eyebrow>Your Cart</Eyebrow>
          <Typography className="serif" variant="h2" sx={{ mt: 1 }}>
            Review your order
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ maxWidth: 1300, py: 4, px: { xs: 2, md: 4 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
            gap: 3,
            width: '100%',
            alignItems: 'start',
          }}
        >
          {/* Items */}
          <Box>
            <Stack spacing={1.75}>
              {items.map((it) => (
                <Box
                  key={`${it.type}-${it.id}`}
                  sx={{
                    bgcolor: '#fff',
                    border: '1px solid var(--border-l)',
                    borderRadius: '16px',
                    p: { xs: 2, md: 2.5 },
                    display: 'grid',
                    gridTemplateColumns: { xs: '48px 1fr auto', md: '56px 1fr auto auto' },
                    gap: { xs: 1.5, md: 2.25 },
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 48, md: 56 },
                      height: { xs: 48, md: 56 },
                      borderRadius: '14px',
                      bgcolor: 'var(--mint)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: { xs: 22, md: 26 },
                    }}
                  >
                    {it.icon || '🔬'}
                  </Box>
                  <Box sx={{ minWidth: 0, gridColumn: { xs: '2 / 3', md: 'auto' } }}>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        color: it.type === 'pkg' ? 'var(--sage)' : 'var(--amber)',
                        mb: 0.25,
                      }}
                    >
                      {it.type === 'pkg' ? 'Package' : 'Test'}
                    </Typography>
                    <Typography
                      sx={{ fontSize: { xs: 14, md: 15 }, fontWeight: 700, color: 'var(--ink)', mb: 0.25, lineHeight: 1.3 }}
                    >
                      {it.name}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'var(--stone)', fontWeight: 300 }}>
                      {it.paramCount} parameter{it.paramCount > 1 ? 's' : ''} · Report in {it.tat}
                    </Typography>
                  </Box>
                  <Stack
                    alignItems="flex-end"
                    spacing={0.25}
                    sx={{ gridColumn: { xs: '2 / 3', md: 'auto' }, gridRow: { xs: 2, md: 'auto' }, justifySelf: { xs: 'start', md: 'end' } }}
                  >
                    <Typography className="serif" sx={{ fontSize: { xs: 18, md: 20 }, fontWeight: 700, color: 'var(--forest)' }}>
                      ₹{it.price.toLocaleString()}
                    </Typography>
                    {it.original && it.original > it.price && (
                      <Typography
                        sx={{ fontSize: 12, textDecoration: 'line-through', color: 'var(--stone)' }}
                      >
                        ₹{it.original.toLocaleString()}
                      </Typography>
                    )}
                  </Stack>
                  <IconButton
                    onClick={() => {
                      removeItem(it.id, it.type);
                      showToast('Removed from cart');
                    }}
                    sx={{
                      color: 'var(--stone)',
                      gridColumn: { xs: '3 / 4', md: 'auto' },
                      gridRow: { xs: '1 / 3', md: 'auto' },
                    }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Box>
              ))}
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3 }}>
              <Button onClick={() => navigate(ROUTES.TESTS)} sx={{ color: 'var(--sage)', fontWeight: 700 }}>
                ← Continue shopping
              </Button>
              <Button
                onClick={() => {
                  clearCart();
                  showToast('Cart cleared');
                }}
                sx={{ color: 'var(--stone)', fontSize: 13 }}
              >
                Clear cart
              </Button>
            </Stack>
          </Box>

          {/* Summary */}
          <Box>
            <Box
              sx={{
                bgcolor: '#fff',
                border: '1.5px solid var(--border-l)',
                borderRadius: '20px',
                p: 3.25,
                position: { md: 'sticky' },
                top: 90,
              }}
            >
              <Typography className="serif" variant="h3" sx={{ mb: 2.5 }}>
                Order Summary
              </Typography>
              <Stack spacing={1.75} sx={{ pb: 2, borderBottom: '1px dashed var(--border-l)' }}>
                <Row k={`Subtotal (${totals.count} item${totals.count > 1 ? 's' : ''})`} v={`₹${totals.subtotal.toLocaleString()}`} />
                <Row k="You save" v={`-₹${totals.savings.toLocaleString()}`} highlight="green" />
                <Row k="Home collection" v="Free" highlight="green" />
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ pt: 2, mb: 3 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Total</Typography>
                <Typography className="serif" sx={{ fontSize: 28, fontWeight: 700, color: 'var(--forest)' }}>
                  ₹{grand.toLocaleString()}
                </Typography>
              </Stack>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                onClick={() => navigate(ROUTES.BOOKING)}
                sx={{ mb: 1.5 }}
              >
                Proceed to Booking →
              </Button>
              <Typography sx={{ fontSize: 11, color: 'var(--stone)', textAlign: 'center', fontWeight: 300 }}>
                NABL verified · Doctor-signed reports · 60 min Trustian
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

const Row = ({ k, v, highlight }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography sx={{ fontSize: 13, color: 'var(--charcoal)' }}>{k}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: highlight === 'green' ? 'var(--sage)' : 'var(--ink)' }}>
      {v}
    </Typography>
  </Stack>
);

export default Cart;
